import { useState } from 'react';
import PixelTransition from '@/components/base/PixelTransition/PixelTransition';

export default function HeroSection() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!email) {
      setStatus('error');
      setMessage('Please enter your email address.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setStatus('error');
      setMessage('Please enter a valid email address.');
      return;
    }

    setStatus('loading');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const res = await fetch('https://readdy.ai/api/form/d8plnv0n4gr6p3b2fvk0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (res.ok) {
        setStatus('success');
        setMessage('Thanks — you are on the list.');
        setEmail('');
      } else {
        setStatus('error');
        setMessage('Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setMessage('Network error. Please try again.');
    }
  };

  return (
    <section className="relative w-full h-screen min-h-[720px] bg-white overflow-hidden flex">
      {/* LEFT COLUMN */}
      <div className="relative flex-1 flex flex-col justify-between px-8 md:px-14 lg:px-20 py-10 md:py-14">
        {/* Top: brand */}
        <div className="flex items-center justify-between">
          <a
            href="/"
            className="font-display font-black text-brand-blue text-2xl md:text-3xl tracking-tight cursor-pointer"
            style={{ fontFamily: '"Pacifico", cursive' }}
          >
            Stremé
          </a>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-brand-blue">
            <a href="#about" className="cursor-pointer hover:opacity-70 whitespace-nowrap">About</a>
            <a href="#collection" className="cursor-pointer hover:opacity-70 whitespace-nowrap">Collection</a>
            <a href="#contact" className="cursor-pointer hover:opacity-70 whitespace-nowrap">Contact</a>
          </nav>
        </div>

        {/* Headline */}
        <div className="mt-10 md:mt-0">
          <h1 className="font-display font-semibold text-brand-blue uppercase leading-[0.95] tracking-[-0.02em] text-[56px] md:text-[88px] lg:text-[110px] xl:text-[128px]">
            A New Era
            <br />
            <span className="font-serif italic font-normal lowercase pr-3">in</span>
            Living
            <br />
            Design
          </h1>

          {/* Status badge */}
          <div className="flex items-center gap-3 mt-6 md:mt-8">
            <span className="w-3 h-3 md:w-3.5 md:h-3.5 rounded-full bg-brand-red inline-block" />
            <span className="text-brand-red text-base md:text-lg font-medium tracking-wide">
              Stremé Design Launching Soon
            </span>
          </div>
        </div>

        {/* Newsletter Form */}
        <div className="w-full max-w-[520px] mt-12 md:mt-0">
          <div className="flex items-center gap-2 mb-4">
            <span className="h-px w-8 bg-brand-blue/40" />
            <p className="text-brand-blue/80 text-xs md:text-sm font-medium tracking-[0.25em] uppercase">
              Join the List
            </p>
          </div>

          <p className="text-brand-blue text-xl md:text-2xl font-display font-medium leading-snug mb-6">
            Be the first to receive early access &amp; private previews.
          </p>

          <form
            id="hero-newsletter-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="w-full"
          >
            <div className="relative w-full border-b border-brand-blue/40 focus-within:border-brand-blue transition-colors">
              <input
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full bg-transparent text-brand-blue text-base md:text-lg font-medium placeholder:text-brand-blue/40 py-3 pr-14 focus:outline-none"
                autoComplete="email"
                maxLength={120}
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                aria-label="Subscribe"
                className="absolute right-0 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-brand-blue/40 flex items-center justify-center text-brand-blue cursor-pointer hover:bg-brand-blue hover:text-white hover:border-brand-blue transition-all duration-300 disabled:opacity-50 group"
              >
                {status === 'loading' ? (
                  <i className="ri-loader-4-line text-lg animate-spin flex items-center justify-center w-5 h-5" />
                ) : (
                  <i className="ri-arrow-right-up-line text-lg flex items-center justify-center w-5 h-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                )}
              </button>
            </div>

            {message ? (
              <p
                className={`text-xs md:text-sm mt-3 ${
                  status === 'success' ? 'text-green-600' : status === 'error' ? 'text-brand-red' : 'text-brand-blue'
                }`}
              >
                {message}
              </p>
            ) : (
              <p className="text-xs text-brand-blue/60 mt-3 leading-relaxed">
                By subscribing, you agree to receive occasional updates from Stremé. Unsubscribe anytime.
              </p>
            )}
          </form>
        </div>
      </div>

      {/* RIGHT COLUMN */}
      <div className="relative hidden md:flex w-[58%] lg:w-[55%] xl:w-[54%]">
        {/* Vertical blue strip with marquee text */}
        <div className="relative w-[64px] lg:w-[72px] bg-brand-blue overflow-hidden">
          <div className="vertical-strip-text absolute left-1/2 top-1/2 whitespace-nowrap text-white font-display font-medium tracking-[0.18em] text-base lg:text-lg uppercase flex">
            <span className="mx-6">A Unveiling</span>
            <span className="mx-3">—</span>
            <span className="mx-6">Lumen 240.S</span>
            <span className="mx-3">—</span>
            <span className="mx-6">Stremé Lighting</span>
            <span className="mx-3">—</span>
            <span className="mx-6">A Unveiling</span>
            <span className="mx-3">—</span>
            <span className="mx-6">Lumen 240.S</span>
            <span className="mx-3">—</span>
            <span className="mx-6">Stremé Lighting</span>
            <span className="mx-3">—</span>
          </div>
        </div>

        {/* Product image area with pixel transition */}
        <div className="relative flex-1 bg-[#e9eaec] overflow-hidden">
          <PixelTransition
            gridSize={14}
            pixelColor="#1b2a6b"
            animationStepDuration={0.45}
            aspectRatio="0%"
            className="!w-full !h-full absolute inset-0"
            style={{ width: '100%', height: '100%' }}
            firstContent={
              <img
                src="https://images.pexels.com/photos/15253319/pexels-photo-15253319.jpeg?auto=compress&cs=tinysrgb&w=1200"
                alt="Stremé Lumen 240.S — sculptural artistic table lamp with blue shade"
                title="Stremé Lumen 240.S — A New Era in Living Design"
                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
              />
            }
            secondContent={
              <img
                src="https://images.pexels.com/photos/30654322/pexels-photo-30654322.jpeg?auto=compress&cs=tinysrgb&w=1200"
                alt="Stremé Lumen 240.S — conical sculptural lamp arrangement in ambient interior"
                title="Stremé Lumen 240.S — Editorial Atmosphere"
                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
              />
            }
          />

          {/* Circular badge top-right */}
          <div className="absolute top-6 right-6 lg:top-8 lg:right-8 w-14 h-14 lg:w-16 lg:h-16 rounded-full bg-white/70 backdrop-blur-sm flex items-center justify-center cursor-pointer hover:bg-white transition z-10">
            <i className="ri-arrow-right-up-line text-brand-blue text-2xl lg:text-3xl flex items-center justify-center w-6 h-6" />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes verticalStripScroll {
          0% { transform: translate(-50%, -50%) rotate(-90deg) translateX(0); }
          100% { transform: translate(-50%, -50%) rotate(-90deg) translateX(-50%); }
        }
        .vertical-strip-text {
          transform-origin: center center;
          transform: translate(-50%, -50%) rotate(-90deg);
          animation: verticalStripScroll 28s linear infinite;
          will-change: transform;
        }
      `}</style>
    </section>
  );
}